import { environment } from "@/environments/environment";
import type { ChatMention } from "@/features/chat/shared/types";

export type MentionSegment =
  | { type: "text"; value: string }
  | { type: "mention"; value: string; url: string };

/**
 * Where a mentioned creator's profile lives, for this environment.
 *
 * Not the `url` the server puts on the mention: that one is absolute and built from
 * messaging-service's own configured domain, so following it from staging navigates to
 * production. The username is encoded because a value containing "//" would otherwise
 * make the target protocol-relative and send the creator off-site.
 */
/** Punctuation the mention charset swallows because it has to accept dots. */
const TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;

const profileUrl = (username: string) =>
  `${environment.creatorProfileUrl}/${encodeURIComponent(username)}`;

/**
 * Splits a message body into text and the creators it names.
 *
 * Driven by the server's `mentions` rather than by the text: only a mention the server
 * resolved becomes a link, so an `@word` that merely looks like a handle — a typo, the
 * domain half of an email address, or the placeholder standing in for a creator who is
 * gone — stays plain text.
 *
 * The charset matches the wider one the server accepts, so a dotted handle like
 * `@maya.official` finds its own entry. It used to stop at the dot, which meant a
 * dotted handle silently rendered as text however well the server had resolved it.
 */
export const parseMentions = (
  content: string,
  mentions: ChatMention[]
): MentionSegment[] => {
  const resolved = mentions.filter(
    (mention) =>
      (mention.state ?? "resolved") === "resolved" && mention.username
  );

  if (!resolved.length) return [{ type: "text", value: content }];

  const mentionMap = new Map(
    resolved.map((mention) => [
      mention.username.toLowerCase(),
      mention.username
    ])
  );

  const parts = content.split(/(@[a-zA-Z0-9_.-]+)/g);
  const segments: MentionSegment[] = [];

  const pushText = (value: string) => {
    if (!value) return;

    // Coalesce runs of text so a mention followed by punctuation reads as one
    // segment rather than as the leftovers of a split.
    const last = segments[segments.length - 1];
    if (last?.type === "text") {
      last.value += value;

      return;
    }

    segments.push({ type: "text", value });
  };

  for (const part of parts) {
    if (!part) continue;

    if (!part.startsWith("@")) {
      pushText(part);
      continue;
    }

    const handle = part.slice(1);
    const exact = mentionMap.get(handle.toLowerCase());
    if (exact) {
      segments.push({ type: "mention", value: part, url: profileUrl(exact) });
      continue;
    }

    // The charset has to accept dots so a dotted handle can match at all, which
    // means it also swallows a sentence's punctuation: "@alice." arrives whole.
    // Retry without the trailing punctuation and hand the punctuation back as text.
    const trimmed = handle.replace(TRAILING_PUNCTUATION, "");
    const resolvedName = trimmed && mentionMap.get(trimmed.toLowerCase());
    if (resolvedName) {
      segments.push({
        type: "mention",
        value: `@${trimmed}`,
        url: profileUrl(resolvedName)
      });
      pushText(handle.slice(trimmed.length));
      continue;
    }

    pushText(part);
  }

  return segments;
};
